import { useState } from 'react';

import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  FileSignature,
  Info,
  Loader2,
  RotateCcw,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';

import { GatingBanner } from '../comum/GatingBanner';
import { useSituacaoOperacionalDoPortal } from '../comum/gating';
import { PortalAccessGuard } from '../comum/PortalAccessGuard';
import { useBuscarTitularPorCpf } from './dados';
import { OrientacaoDtpAlert } from './OrientacaoDtpAlert';

function mascararCpf(valor: string): string {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);
  return digitos
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/**
 * `/credenciado/cooperativa/inscrever-terceiro` — a cooperativa inscreve um
 * veículo/condutor de terceiro em nome de um titular já cadastrado.
 *
 * Fluxo: busca por CPF → um dos quatro achados possíveis (titular completo,
 * não localizado, ou localizado com e-mail/telefone/licença faltando) →
 * confirmação → conclusão. Dado mockado — ver `dados.ts` e `../comum/mock.ts`
 * para a justificativa. Exclusiva de Cooperativa, pela guarda `requireCoop`,
 * igual ao protótipo: só cooperativa inscreve terceiro em nome de um titular
 * já cadastrado.
 *
 * A busca lê o mock; o "Concluir inscrição" não grava nada. Sem API de
 * inscrição, ele mostra o aviso de cadastro indisponível, sem gerar
 * protocolo nem mostrar cartão de sucesso: CLAUDE.md, seção "Proibido", só
 * autoriza mock de leitura, e toda ação de gravação mostra o aviso, nunca
 * finge gravar (mesmo padrão de `operacao/condutores/CondutorFormulario.tsx`).
 * Um aviso fixo acompanha o botão, no cartão do titular, porque aviso
 * passageiro some e a captura de tela ficaria parecendo inscrição feita (mesmo
 * padrão de `solicitacoes/NovaSolicitacaoPage.tsx`).
 */
export function InscreverTerceiroPage() {
  return (
    <PortalAccessGuard regra="requireCoop">
      <InscricaoDeTerceiro />
    </PortalAccessGuard>
  );
}

function InscricaoDeTerceiro() {
  const navigate = useNavigate();
  const [cpf, setCpf] = useState('');
  const [buscou, setBuscou] = useState(false);

  const busca = useBuscarTitularPorCpf();
  const { data: situacaoOperacional } = useSituacaoOperacionalDoPortal();

  const bloqueado = situacaoOperacional?.bloqueia ?? false;
  const motivoDoBloqueio = bloqueado ? situacaoOperacional?.titulo : undefined;

  const cpfLimpo = cpf.replace(/\D/g, '');
  const cpfValido = cpfLimpo.length === 11;
  const resultado = buscou && busca.isSuccess ? busca.data : undefined;

  function consultar() {
    if (!cpfValido || bloqueado) return;
    setBuscou(true);
    busca.mutate(cpfLimpo);
  }

  function trocarCpf() {
    setBuscou(false);
    setCpf('');
    busca.reset();
  }

  function concluirInscricao() {
    if (bloqueado) return;
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  return (
    <div className="max-w-2xl space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 gap-1.5"
        onClick={() => navigate('/credenciado/cooperados')}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Voltar
      </Button>

      <PageHeader
        title="Inscrever terceiros"
        description="Inicie a inscrição de um veículo e condutor de terceiro em nome de um titular já cadastrado."
      />

      <GatingBanner />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSignature className="text-primary h-4 w-4" aria-hidden="true" />
                Buscar titular
              </CardTitle>
              <CardDescription>
                Informe o CPF do titular do veículo para localizar os dados dele no cadastro do DTP.
              </CardDescription>
            </div>
            <Badge variant="secondary" className="shrink-0 gap-1">
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
              Cooperativa
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cpf-titular">CPF do titular</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="cpf-titular"
                inputMode="numeric"
                placeholder="000.000.000-00"
                maxLength={14}
                value={mascararCpf(cpf)}
                onChange={(evento) => setCpf(evento.target.value)}
                disabled={buscou}
                className="font-mono sm:flex-1"
                autoComplete="off"
              />
              {!buscou ? (
                <Button
                  onClick={consultar}
                  disabled={!cpfValido || bloqueado}
                  title={bloqueado ? motivoDoBloqueio : undefined}
                  className="gap-2"
                >
                  <Search className="h-4 w-4" aria-hidden="true" />
                  Consultar
                </Button>
              ) : (
                <Button variant="outline" onClick={trocarCpf} className="gap-2">
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Trocar CPF
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {busca.isPending && (
        <div
          role="status"
          aria-live="polite"
          className="text-muted-foreground flex items-center justify-center gap-2 py-8 text-sm"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Consultando cadastro…
        </div>
      )}

      {busca.isError && (
        <ErrorState
          variant="load-failed"
          description="Não foi possível consultar o cadastro agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => busca.mutate(cpfLimpo) }}
        />
      )}

      {resultado && resultado.situacao !== 'encontrado' && (
        <OrientacaoDtpAlert situacao={resultado.situacao} onTentarOutroCpf={trocarCpf} />
      )}

      {resultado && resultado.situacao === 'encontrado' && resultado.titular && (
        <Card className="border-success/30 bg-success/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="text-success h-4 w-4" aria-hidden="true" />
              Titular localizado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground text-xs">Nome</dt>
                <dd className="font-medium">{resultado.titular.nome}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">CPF</dt>
                <dd className="font-mono">{mascararCpf(resultado.titular.cpf)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">E-mail</dt>
                <dd>{resultado.titular.email ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Telefone</dt>
                <dd>{resultado.titular.telefone ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">CNH</dt>
                <dd>
                  {resultado.titular.cnh_numero} ({resultado.titular.cnh_categoria})
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Veículo</dt>
                <dd>
                  {resultado.titular.veiculo_placa} — {resultado.titular.veiculo_modelo}
                </dd>
              </div>
            </dl>

            <Alert>
              <AlertTitle className="text-sm">Responsabilidade da cooperativa</AlertTitle>
              <AlertDescription className="text-xs">
                Ao concluir esta inscrição, a cooperativa declara possuir autorização do titular
                para representá-lo neste credenciamento.
              </AlertDescription>
            </Alert>

            <Alert>
              <Info className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                A inscrição de terceiros ainda não está disponível. Você pode buscar o titular e
                conferir os dados dele, mas nada é registrado.
              </AlertDescription>
            </Alert>

            <Button
              onClick={concluirInscricao}
              disabled={bloqueado}
              title={bloqueado ? motivoDoBloqueio : undefined}
              className="w-full gap-2"
              size="lg"
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
              Concluir inscrição
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default InscreverTerceiroPage;
